import { useTranslations } from '@aikoa/i18n/react'
import { useRouter } from 'expo-router'
import { useCallback } from 'react'
import { View } from 'react-native'
import ContentBox, { BoxActionsHeader } from '~/components/layout/ContentBox'
import { LeadListItem } from '~/components/leads/LeadListItem'
import { useLeadStore } from '~/components/leads/leadStore'
import { LoadingView } from '~/components/LoadingView'
import { Body, Detail } from '~/components/typography/Content'
import { Heading1, Heading3 } from '~/components/typography/Titles'
import { Badge } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import { useSetTabCount } from '~/components/ui/tabs'
import { usePropertyLeads } from '~/data/leads/useLeads'
import { cn } from '~/lib/utils'
import { ActionButton } from '../Actions'

interface PropertyLeadsProps {
  assetId: string
  className?: string
  showTitleCount?: boolean
  hasPrice?: boolean
}

export function PropertyLeads({
  assetId,
  className,
  showTitleCount = false,
  hasPrice = true,
}: PropertyLeadsProps) {
  const { t } = useTranslations(['property', 'common', 'feedback'])
  const setOfferAssetId = useLeadStore((state) => state.setOfferAssetId)

  const { leads, matchCount, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    usePropertyLeads(assetId)

  const hasNoMatches = matchCount === 0

  useSetTabCount('leads', matchCount)
  const router = useRouter()

  const handleLeadPress = useCallback(
    (leadId: string) => {
      setOfferAssetId(assetId)
      router.push({
        pathname: '/leads/[leadId]',
        params: { leadId, view: 'matches' },
      })
    },
    [setOfferAssetId, assetId, router]
  )

  return (
    <ContentBox
      scrollable={false}
      contentContainerClassName="gap-4 p-0 pb-6"
      header={
        <BoxActionsHeader variant="default">
          <View className="flex-row items-center justify-center gap-2">
            <Heading1 className="text-center">{t(($) => $.leads.title)}</Heading1>
            {showTitleCount && matchCount > 0 && (
              <Badge
                size="sm"
                variant="secondary"
                className="h-6 min-w-6 items-center justify-center px-1 py-0 text-xs">
                {String(matchCount)}
              </Badge>
            )}
          </View>
          <Body className="text-center">{t(($) => $.leads.description)}</Body>
        </BoxActionsHeader>
      }>
      <View className={cn('gap-4', className)}>
        {isLoading ? (
          <LoadingView />
        ) : hasNoMatches ? (
          <View className="flex-1 items-center justify-center gap-6 py-8">
            <View className="w-full flex-1 items-center justify-center gap-2">
              <Heading3 className="text-center text-foreground">
                {t(($) => $.leads.noLeads)}
              </Heading3>
              <Detail className="px-6 text-center text-foreground">
                {hasPrice
                  ? t(($) => $.leads.noLeadsDescription)
                  : t(($) => $.common.inboundEmail.notifications.ingest.propertyPriceHint)}
              </Detail>
            </View>
            <ActionButton
              action={{
                key: 'create-leads',
                label: t(($) => $.feedback.requestFlow.createNewTitle),
                onPress: () => router.push('/(authenticated)/feedback-request/add'),
                buttonVariant: 'primary',
                buttonSize: 'lg',
              }}
            />
          </View>
        ) : (
          <View className="gap-4 px-3">
            {leads.map((lead) => (
              <LeadListItem
                key={lead.id}
                lead={{
                  id: lead.id,
                  name: lead.name ?? t(($) => $.property.leads.contact),
                  interest: {
                    id: lead.interest.id ?? undefined,
                    data: lead.interest.data,
                  },
                  media: lead.media,
                  matchScores: lead.matchScores,
                  is_own: lead.is_own,
                  is_verified: true,
                  is_purchased: lead.is_purchased,
                }}
                onPress={() => handleLeadPress(lead.id)}
              />
            ))}

            {hasNextPage && (
              <Button
                variant="outline"
                onPress={() => void fetchNextPage()}
                loading={isFetchingNextPage}
                className="mt-3 self-center">
                {t(($) => $.common.loadMore)}
              </Button>
            )}
          </View>
        )}
      </View>
    </ContentBox>
  )
}
